/* eslint-disable no-restricted-syntax */
/**
 * Generic color model with helpers for converting to and from common color
 *  formats used within this project.
 */
import type { RGBA } from '@onxmaps/markups-web'

class Color {
  red: number
  green: number
  blue: number
  alpha: number

  /**
   * Initializes a Color.
   * @param red - Red value from 0 to 255.
   * @param green - Green value from 0 to 255.
   * @param blue - Blue value from 0 to 255.
   * @param alpha - Alpha rate from 0.0 to 1.0.
   * @throws {Error} if any of the color values are not a number.
   */
  constructor(red: number, green: number, blue: number, alpha: number) {
    Object.entries({ red, green, blue, alpha }).forEach(([k, v]) => {
      if (isNaN(v)) {
        throw Error(`Color ${k} value is not a number`)
      }
    })

    this.red = red
    this.green = green
    this.blue = blue
    this.alpha = alpha
  }

  /**
   * Creates a color from an RGBA Array.
   * @param param1 - RGBA array of values. Colors are integers from 0 to 255 and alpha is a float value from 0 to 1.
   */
  static fromRgbaArray([red, green, blue, alpha]: number[]): Color {
    return new Color(red, green, blue, alpha)
  }

  /**
   * Creates a color from an RGBA Object.
   */
  static fromRgbaObject({ r, g, b, a }: RGBA): Color {
    return new Color(r, g, b, a ?? 1)
  }

  /**
   * Creates a color from a formatted string "rgba(r, g, b, a)".
   * @param rgbaStr - Formatted string.
   */
  static fromRgbaString(rgbaStr: string): Color {
    const explodedColorCode = rgbaStr.split(/[(),]/)
    const red = parseInt(explodedColorCode[1].trim())
    const green = parseInt(explodedColorCode[2].trim())
    const blue = parseInt(explodedColorCode[3].trim())
    const alpha = parseFloat(explodedColorCode[4].trim())
    return Color.fromRgbaArray([red, green, blue, alpha])
  }

  /**
   * Creates a color from a formatted string "r,g,b,a" or "r,g,b"
   * @param colorStr - Formatted string.
   */
  static fromString(colorStr: string): Color {
    const colorArr = colorStr.split(',').map((v) => Number(v))
    if (colorArr.length < 3) {
      throw new Error(`Could not create color from string: ${colorStr}`)
    }

    if (colorArr.length === 3) {
      // Assume fully opaque
      colorArr.push(1)
    }

    return Color.fromRgbaArray(colorArr as [number, number, number, number])
  }

  /**
   * Creates a color from a hex formatted string "#rrggbb" or "#rgb"
   * @param hexStr - hex formatted string
   */
  static fromHex(hexStr: string): Color {
    // remove hash
    const hex = hexStr.slice(1)
    let hexRed, hexGreen, hexBlue

    // pull out hex values
    const isShortVersion = hex.length === 3
    if (isShortVersion) {
      hexRed = hex.charAt(0) + hex.charAt(0)
      hexGreen = hex.charAt(1) + hex.charAt(1)
      hexBlue = hex.charAt(2) + hex.charAt(2)
    } else {
      hexRed = hex.substring(0, 2)
      hexGreen = hex.substring(2, 4)
      hexBlue = hex.substring(4, 6)
    }

    // convert to decimal values
    const decRed = parseInt(hexRed, 16)
    const decGreen = parseInt(hexGreen, 16)
    const decBlue = parseInt(hexBlue, 16)

    return new Color(decRed, decGreen, decBlue, 1)
  }

  /**
   * Compares values of each color to determine whether the two Color objects
   *  represent the same color.
   * @param other - Other color to compare values with.
   */
  equals(other: Color): boolean {
    return (
      this.red === other.red &&
      this.green === other.green &&
      this.blue === other.blue &&
      this.alpha === other.alpha
    )
  }

  /**
   * Returns this color as an RGBA array.
   */
  toRgbaArray(): [number, number, number, number] {
    return [this.red, this.green, this.blue, this.alpha]
  }

  /**
   * Returns this color as an RGBA object.
   */
  toRgbaObject(): RGBA {
    return { r: this.red, g: this.green, b: this.blue, a: this.alpha }
  }

  /**
   * Returns this color as an RGBA string.
   */
  toRgbaString(): string {
    return `rgba(${this.red}, ${this.green}, ${this.blue}, ${this.alpha})`
  }

  /**
   * Calculates the relative luminance of this color in the range [0, 1]. This is a measure of how bright the color
   * appears, adjusted for human vision.
   */
  getRelativeLuminance(): number {
    const [r, g, b] = [this.red, this.green, this.blue].map((c) => {
      return c <= 10.0164
        ? c / 3294.6
        : Math.pow((c / 255 + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }

  /**
   * Formats this color as a comma-separated string.
   */
  toString(): string {
    return `${this.red},${this.green},${this.blue},${this.alpha}`
  }

  /**
   * Formats the color as a hex formatted string "#rrggbb"
   */
  toHex(): string {
    const redHex = this.red.toString(16)
    const greenHex = this.green.toString(16)
    const blueHex = this.blue.toString(16)

    return `#${redHex.length === 1 ? `0${redHex}` : redHex}${
      greenHex.length === 1 ? `0${greenHex}` : greenHex
    }${blueHex.length === 1 ? `0${blueHex}` : blueHex}`
  }

  /**
   * Returns this color as an RGB string (without alpha channel).
   */
  toRgbString(): string {
    return `rgb(${this.red}, ${this.green}, ${this.blue})`
  }

  /**
   * Lightens the color by blending toward white.
   * @param amount - Amount to lighten (0.0 to 1.0). 0 = no change, 1 = white.
   */
  lighten(amount: number): Color {
    const adjR = Math.floor(this.red + (255 - this.red) * amount)
    const adjG = Math.floor(this.green + (255 - this.green) * amount)
    const adjB = Math.floor(this.blue + (255 - this.blue) * amount)
    return new Color(adjR, adjG, adjB, this.alpha)
  }

  /**
   * Darkens the color by blending toward black.
   * @param amount - Amount to darken (0.0 to 1.0). 0 = no change, 1 = black.
   */
  darken(amount: number): Color {
    const adjR = Math.floor(this.red * (1 - amount))
    const adjG = Math.floor(this.green * (1 - amount))
    const adjB = Math.floor(this.blue * (1 - amount))
    return new Color(adjR, adjG, adjB, this.alpha)
  }

  /**
   * Adjusts the color brightness. Positive values lighten, negative values darken.
   * @param amount - Adjustment amount (-1.0 to 1.0).
   *                          Positive = lighten, Negative = darken, 0 = no change.
   */
  adjust(amount: number): string {
    if (amount >= 0) {
      return this.lighten(amount).toRgbString()
    }
    return this.darken(Math.abs(amount)).toRgbString()
  }
}

/* Base colors */
export const baseOrangeColor = new Color(255, 51, 0, 1)
export const baseBlueColor = new Color(8, 121, 255, 1)
export const baseYellowColor = new Color(245, 166, 35, 1)
export const baseGreenColor = new Color(96, 129, 27, 1)

export const themeColor = {
  hunt: baseOrangeColor.toHex(),
  backcountry: baseBlueColor.toHex(),
  offroad: baseYellowColor.toHex(),
  // FUTURE - use corrected green?
  fish: baseBlueColor.toHex(),
}

export default Color
